// Synthesized scan feedback tones (Web Audio — no asset files). Five "good" and five "bad"
// variants, chosen + volume-controlled from Settings › General. Shared by the POS screen
// (real scans) and the settings page (preview buttons).

// Variant 6 of every list is a real audio file (public/sounds/*.wav) instead of a synthesized tone.
export const WAV_VARIANT = 6;
const WAV_FILES = { success: '/sounds/ok.wav', approved: '/sounds/ok.wav', error: '/sounds/error.wav', declined: '/sounds/error.wav' };

export const SUCCESS_SOUNDS = [
  { id: 1, name: 'Classic Beep' },
  { id: 2, name: 'Double Chirp' },
  { id: 3, name: 'Soft Ding' },
  { id: 4, name: 'Rising Tone' },
  { id: 5, name: 'Register Click' },
  { id: WAV_VARIANT, name: 'OK (sound file)' },
];

export const DECLINED_SOUNDS = [
  { id: 1, name: 'Descending Tone' },
  { id: WAV_VARIANT, name: 'Error (sound file)' },
];

export const APPROVED_SOUNDS = [
  { id: 1, name: 'Cash Register' },
  { id: 2, name: 'Triple Chime' },
  { id: 3, name: 'Success Fanfare' },
  { id: 4, name: 'Soft Bell' },
  { id: 5, name: 'Coin Drop' },
  { id: WAV_VARIANT, name: 'OK (sound file)' },
];

export const ERROR_SOUNDS = [
  { id: 1, name: 'Triple Buzz' },
  { id: 2, name: 'Low Horn' },
  { id: 3, name: 'Falling Tone' },
  { id: 4, name: 'Alarm Pulse' },
  { id: 5, name: 'Sharp Bonk' },
  { id: WAV_VARIANT, name: 'Error (sound file)' },
];

let ctx = null;
const audio = () => {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
};

// One tone: [frequencyHz, startSec, durationSec, waveform?, endFrequencyHz?]
const tone = (c, gainNode, [freq, start, dur, type = 'sine', endFreq]) => {
  const osc = c.createOscillator();
  const env = c.createGain();
  osc.type = type;
  const t0 = c.currentTime + start;
  osc.frequency.setValueAtTime(freq, t0);
  if (endFreq) osc.frequency.linearRampToValueAtTime(endFreq, t0 + dur);
  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(1, t0 + 0.008);
  env.gain.setValueAtTime(1, t0 + Math.max(0.01, dur - 0.03));
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(env);
  env.connect(gainNode);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
};

const PATTERNS = {
  declined: {
    1: [[420, 0, 0.18, 'sawtooth'], [300, 0.2, 0.22, 'sawtooth'], [220, 0.44, 0.4, 'sawtooth']],
  },
  approved: {
    1: [[1200, 0, 0.06, 'square'], [1800, 0.07, 0.10, 'square'], [2400, 0.18, 0.16, 'square']],
    2: [[880, 0, 0.12, 'sine'], [1100, 0.14, 0.12, 'sine'], [1320, 0.28, 0.22, 'sine']],
    3: [[660, 0, 0.09, 'triangle'], [880, 0.1, 0.09, 'triangle'], [1100, 0.2, 0.09, 'triangle'], [1320, 0.3, 0.3, 'triangle']],
    4: [[1568, 0, 0.5, 'sine'], [2093, 0.05, 0.45, 'sine']],
    5: [[2600, 0, 0.04, 'square'], [2200, 0.06, 0.04, 'square'], [2900, 0.13, 0.05, 'square'], [2400, 0.2, 0.12, 'sine']],
  },
  success: {
    1: [[1800, 0, 0.09, 'square']],
    2: [[1500, 0, 0.06, 'square'], [2000, 0.09, 0.06, 'square']],
    3: [[1320, 0, 0.22, 'sine'], [2640, 0, 0.12, 'sine']],
    4: [[900, 0, 0.14, 'triangle', 1700]],
    5: [[2200, 0, 0.03, 'square'], [1100, 0.035, 0.05, 'triangle']],
  },
  error: {
    1: [[400, 0, 0.12, 'square'], [400, 0.2, 0.12, 'square'], [400, 0.4, 0.12, 'square']],
    2: [[180, 0, 0.45, 'sawtooth']],
    3: [[900, 0, 0.35, 'square', 250]],
    4: [[660, 0, 0.08, 'square'], [520, 0.1, 0.08, 'square'], [660, 0.2, 0.08, 'square'], [520, 0.3, 0.08, 'square']],
    5: [[300, 0, 0.18, 'sawtooth', 120]],
  },
};

function playWav(kind, volume) {
  const el = new Audio(`${process.env.PUBLIC_URL || ''}${WAV_FILES[kind] || WAV_FILES.success}`);
  el.volume = Math.max(0, Math.min(100, Number(volume))) / 100;
  el.play().catch(() => {});
}

export function playScanSound(kind, variant = 1, volume = 70) {
  if (Number(volume) <= 0) return;
  if (Number(variant) === WAV_VARIANT) { playWav(kind, volume); return; }
  try {
    const c = audio();
    const master = c.createGain();
    master.gain.value = Math.max(0, Math.min(100, Number(volume))) / 100 * (kind === 'error' ? 0.6 : 0.45);
    master.connect(c.destination);
    const pattern = PATTERNS[kind]?.[Number(variant)] || PATTERNS[kind][1];
    pattern.forEach((p) => tone(c, master, p));
  } catch (e) {
    console.error('scan sound failed:', e);
  }
}

// Convenience: reads the store settings object saved from Settings › General.
export function playScanFeedback(storeSettings, kind) {
  if (!storeSettings || storeSettings.scan_sound_enabled === false) return;
  const variant = kind === 'error' ? storeSettings.scan_sound_error : storeSettings.scan_sound_success;
  playScanSound(kind, variant || 1, storeSettings.scan_sound_volume ?? 70);
}

// Card payment approved (PAX) — Settings › General › Register Options.
export function playApprovedFeedback(storeSettings) {
  if (!storeSettings || storeSettings.card_approved_sound_enabled === false) return;
  playScanSound('approved', storeSettings.card_approved_sound || 1, storeSettings.card_approved_sound_volume ?? 70);
}

// Card payment declined — descending tone or the Error sound file, chosen in Register Options.
export function playDeclinedFeedback(storeSettings) {
  if (!storeSettings || storeSettings.card_declined_sound_enabled === false) return;
  playScanSound('declined', storeSettings.card_declined_sound || 1, storeSettings.card_declined_sound_volume ?? 80);
}
